import React, { FC, useContext, useState } from "react";
import { Button, Card } from "react-bootstrap";
import { useTranslation } from "react-i18next";
import useFlash from "go-alert/AlertMessage";
import handleError from "go-app/services/errors";
import FormatDate from "go-core/components/Formatters/FormatDate";
import { ReactComponent as TrashSVG } from "go-core/images/svg/actions/rubbish.svg";
import { ReactComponent as FileSVG } from "go-core/images/svg/file.svg";
import { useConfirmation } from "go-form/components/ModalConfirm";
import { PurchaseReceiveApi } from "../../../../../../../../../../services/Api/Organization/types";
import { api } from "../../../../../../../../../../services/Api/api";
import { PurchaseContext } from "../../pages/Preview";
import PurchaseReceiveDetailsModal from "./PurchaseReceiveDetailsModal";

interface Props {
	receives: PurchaseReceiveApi[];
	onHandleUpdate: () => void;
}

const PurchaseReceivesCard: FC<Props> = ({ receives, onHandleUpdate }) => {
	const { t } = useTranslation();
	const { addFlash } = useFlash();
	const confirmation = useConfirmation();
	const { purchase } = useContext(PurchaseContext);
	const [showReceiveDetailsModal, setShowReceiveDetailsModal] = useState<PurchaseReceiveApi | undefined>(undefined);

	const getReceiveItemsTotalQuantity = (receive: PurchaseReceiveApi) => {
		return receive?.items
			?.map((item) => item?.quantity || 0)
			?.reduce((acc, cur) => acc + cur, 0)
			.toFixed(5);
	};

	const onRemove = async (receiveId: number) => {
		try {
			await confirmation({
				title: t("lib:go_form.modal_confirm.title"),
				message: t("lib:go_form.modal_confirm.message.remove"),
			});
			await api.organization().removePurchaseReceives(purchase.id, receiveId);
			onHandleUpdate();
		} catch (err) {
			handleError.alert(err, addFlash);
		}
	};

	return (
		<Card className="card-box">
			<Card.Body>
				<h5 className="card-title">{t("modules.purchase.field.receives.title")}</h5>
				<div className="table-responsive">
					<table className="table table-preview">
						<thead>
							<tr>
								<th>{t("modules.purchase.field.receive_number.title")}</th>
								<th>{t("common.word.reference_number")}</th>
								<th>{t("modules.purchase.field.date.title")}</th>
								<th>{t("common.word.quantity")}</th>
								<th className="action" />
							</tr>
						</thead>
						<tbody>
							{receives?.map((receive, index) => (
								<tr key={`receive-${index}`}>
									<td>
										<div className="d-flex align-items-center">
											<FileSVG className="me-2 file-icon" />
											<a href="javascript:" onClick={() => setShowReceiveDetailsModal(receive)}>
												{receive.number}
											</a>
										</div>
									</td>
									<td>{receive?.reference_number || "-"}</td>
									<td>{FormatDate(receive?.received_at)}</td>
									<td>{getReceiveItemsTotalQuantity(receive)}</td>
									<td className="action">
										<Button variant="add" className="mb-0" onClick={() => onRemove(receive.id)}>
											<TrashSVG className="trash-icon" />
										</Button>
									</td>
								</tr>
							))}
						</tbody>
					</table>
				</div>
				{showReceiveDetailsModal && (
					<PurchaseReceiveDetailsModal
						receive={showReceiveDetailsModal}
						onHide={() => setShowReceiveDetailsModal(undefined)}
						isShown={!!showReceiveDetailsModal}
					/>
				)}
			</Card.Body>
		</Card>
	);
};

export default PurchaseReceivesCard;
